import type { BillingTier } from "@/lib/api/billing";
import type { Envoy } from "@/lib/api/envoy";
import { nextTier } from "@/lib/openings/tier";
import { PLAN_WORDS } from "@/lib/pricing";
import { formatTailors } from "@/lib/overview";

/**
 * Where a person stands on the site, read off the header alone: signed
 * out, a free account, or a plan. One site for all three
 * (design/artboards/SiteMap.dc.html): nothing is hidden or dimmed in any
 * of them, and a gated action opens one sheet when it is pressed.
 */
export type Session = "visitor" | "member" | "pro";

export interface Standing {
    session: Session;
    /** Signed in, on any plan. */
    member: boolean;
    /** On Pro or Max: the gradient plan chip in the tailors pill. */
    pro: boolean;
    /** The plan's name for the chip: Pro or Max; null on Free and signed out. */
    plan: string | null;
    /** The plan one up, for the header's Upgrade: Pro from Free, Max from Pro; null on Max, signed out, or while the plan is read. */
    upgradeTo: "pro" | "max" | null;
    /** Signed in and the envoy is looking: its settings saved, and not resting. */
    envoyOn: boolean;
    /** The tailors left this period, packs included; null until billing says. */
    tailorsLeft: number | null;
    /** The period's own allowance: "of 10". */
    tailorsCap: number | null;
    /** Signed in with none left: the pill turns yellow and Tailor opens the upgrade sheet. */
    tailorsOut: boolean;
    /** Just signed up, and billing is still opening the account: the pill says so instead of a count. */
    settingUp: boolean;
}

/** A visitor's standing: signed out, nothing known. */
export const VISITOR: Standing = {
    session: "visitor",
    member: false,
    pro: false,
    plan: null,
    upgradeTo: null,
    envoyOn: false,
    tailorsLeft: null,
    tailorsCap: null,
    tailorsOut: false,
    settingUp: false,
};

/** Whether the envoy is looking: saved once, and not put to rest. */
export function envoyLooking(envoy: Envoy | null | undefined): boolean {
    return Boolean(envoy && !envoy.paused);
}

/** A signed-in person's standing, from their plan, their envoy and their tailors. */
export function memberStanding({
    tier,
    envoy,
    tailors,
    settingUp = false,
}: {
    tier: BillingTier | undefined;
    envoy: Envoy | null | undefined;
    tailors: { left: number; limit: number } | null;
    settingUp?: boolean;
}): Standing {
    const pro = tier === "pro" || tier === "max";
    return {
        session: pro ? "pro" : "member",
        member: true,
        pro,
        plan: tier === "max" ? "Max" : tier === "pro" ? "Pro" : null,
        upgradeTo: nextTier(tier),
        envoyOn: envoyLooking(envoy),
        tailorsLeft: tailors ? tailors.left : null,
        tailorsCap: tailors ? tailors.limit : null,
        tailorsOut: !settingUp && tailors !== null && tailors.left <= 0,
        settingUp,
    };
}

/**
 * Whether the upgrade sheet puts the plans to this person. Each plan is
 * sold by its tailors a month, and a paid member is told no allowance. A
 * plan still being read may be a paid one, so it counts as one.
 */
export function offersPlans(standing: Standing): boolean {
    return !standing.member || standing.upgradeTo === "pro";
}

/** The envoy chip's words, and nothing more: "Envoy on" or "Envoy off". */
export function envoyLabel(standing: Standing): string {
    return standing.envoyOn ? "Envoy on" : "Envoy off";
}

/** The envoy's state in a word, where the header or the menu already says it is the envoy. */
export function envoyState(standing: Standing): string {
    return standing.envoyOn ? "On" : "Off";
}

/** The tailors pill's words, for a screen reader. */
export function tailorsAria(standing: Standing): string {
    if (standing.settingUp) return "Setting up your account; see your usage";
    if (standing.tailorsOut) return "No tailors left this month; see your usage";
    if (standing.tailorsLeft === null) return "See the plans";
    const plan = standing.plan ? `${standing.plan}: ` : "";
    return `${plan}${formatTailors(standing.tailorsLeft)} of ${formatTailors(standing.tailorsCap ?? 0)} tailors left; see your usage`;
}

/** The model a plan writes on: Advanced on Pro and Max, Standard otherwise. */
export function writerFor(standing: Pick<Standing, "pro">): string {
    return PLAN_WORDS[standing.pro ? "pro" : "free"].name;
}

/**
 * What a tailor takes: the model that writes it, and what it costs. On a
 * plan, the stronger model for it; at none, the way to more. The cost is
 * null until billing says how many are left.
 */
export function tailorTerms(standing: Standing): { model: string; cost: string | null } {
    const model = writerFor(standing);
    if (standing.tailorsOut) return { model, cost: "No tailors left this month. Pro has 25." };
    if (standing.tailorsLeft === null) return { model, cost: null };
    return { model, cost: `Uses 1 tailor · ${formatTailors(standing.tailorsLeft)} available` };
}
